import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { type SxProps, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import React, { type ReactNode, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { accordionStyles, getAccordionSummaryStyles } from '../utils/accordionStyles.ts';

const SPACES_REGEX = /\s/g;

export type ExpandableSectionProps = {
  title: string;
  children: ReactNode;
  enabled?: boolean;
  summary?: ReactNode;
  dataTest?: string;
  subtitle?: string;
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleExpand: () => void;
  showClearButton?: boolean;
  expandableSectionStyles?: {
    accordionDetails: SxProps;
  };
  isExpanded: boolean;
  expandable: boolean;

  showEditButton?: boolean;
  disableEditButton?: boolean;
  onEditClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export const ExpandableSectionWithAccordion = ({
  title,
  children,
  enabled = true,
  expandable = true,
  summary,
  dataTest,
  subtitle,
  showEditButton = false,
  onEditClick,
  onClear,
  showClearButton,
  expandableSectionStyles = {
    accordionDetails: {},
  },
  isExpanded,
  disableEditButton,
  onToggleExpand,
}: ExpandableSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const expandIcon = useMemo(
    () =>
      expandable && (
        <span data-test="accordion-expand-icon-button">
          <ExpandMoreIcon />
        </span>
      ),
    [expandable]
  );

  return (
    <Accordion
      expanded={isExpanded}
      data-test={dataTest || title.toLowerCase().replace(SPACES_REGEX, '-')}
      sx={{
        ...accordionStyles,
        pointerEvents: expandable ? 'auto' : 'none',
      }}
      onChange={onToggleExpand}
    >
      <AccordionSummary
        expandIcon={expandIcon}
        sx={{ ...getAccordionSummaryStyles(expandable, theme) }}
        data-test={'subtitle'}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            display: 'flex',
          }}
        >
          <Typography component="span" variant="h3">
            {title}
          </Typography>

          {subtitle && (
            <Typography color="textSecondary" component="span" data-test="subtitle" sx={{ ml: 3 }} variant="body1">
              {subtitle}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {showEditButton && (
            <Button
              color="primary"
              data-test={`${dataTest || title.toLowerCase().replace(SPACES_REGEX, '-')}-edit-button`}
              onClick={onEditClick}
              sx={{ ml: 2 }}
              variant="text"
              allowed={!disableEditButton}
              notAllowedMessage={t('Editing lookalikes is restricted to organization admins.')}
              component="span"
            >
              {t('Edit')}
            </Button>
          )}

          {showClearButton && (
            <Button
              color="error"
              data-test={`${dataTest || title.toLowerCase().replace(SPACES_REGEX, '-')}-clear-all-button`}
              onClick={onClear}
              variant="text"
              component="span"
            >
              {t('Clear all')}
            </Button>
          )}
        </Stack>
      </AccordionSummary>

      <Divider />

      <AccordionDetails sx={{ py: 3, px: 3, ...expandableSectionStyles?.accordionDetails }}>
        {children}
      </AccordionDetails>

      {!isExpanded && summary && enabled && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {summary}
          </Typography>
        </Box>
      )}
    </Accordion>
  );
};
